import React, { useState, useMemo, useRef, useEffect } from 'react';
import { CheckCircle, XCircle, Clock, UserPlus, ChevronLeft, ChevronRight, CheckSquare, Square, Undo2, FolderPlus, Plus, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { GroupManager } from '@/components/guests/GroupManager';
import { groupService } from '@/lib/api/group.service';
import { Button } from '@/components/ui/button';
import { UserProfilePopover } from '@/components/ui/UserProfilePopover';
import { GroupBadge } from '@/components/guests/GroupBadge';
import { GuestSearchBar } from '@/components/guests/GuestSearchBar';
import type { GuestRow } from '@/components/guests/GuestListTab';
import type { Event } from '@/types/event.types';
import type { GuestGroup, GroupMembership } from '@/types/group.types';

interface GroupsTabProps {
  celebrationId: number;
  events: Event[];
  guestRows: GuestRow[];
  groups: GuestGroup[];
  memberships: GroupMembership[];
  onGroupsChanged: () => void;
  onAddGuests?: () => void;
}

export const GroupsTab: React.FC<GroupsTabProps> = ({
  celebrationId,
  events,
  guestRows,
  groups,
  memberships,
  onGroupsChanged,
  onAddGuests,
}) => {
  const { t } = useTranslation('guests');
  const [managingGroupId, setManagingGroupId] = useState<number | null>(null);
  const [managingGroupName, setManagingGroupName] = useState('');
  const [groupMemberIds, setGroupMemberIds] = useState<number[]>([]);
  const [savingMembers, setSavingMembers] = useState(false);
  const [groupRefreshKey, setGroupRefreshKey] = useState(0);

  // Inline guest table state
  const [activeGroupQuery, setActiveGroupQuery] = useState('');
  const [filteredGuestIds, setFilteredGuestIds] = useState<number[] | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEventIds, setSelectedEventIds] = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  // Guest selection state
  const [selectedContactIds, setSelectedContactIds] = useState<Set<number>>(new Set());
  const [showGroupMenu, setShowGroupMenu] = useState(false);
  const [creatingNewGroup, setCreatingNewGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [savingToGroup, setSavingToGroup] = useState(false);
  const groupMenuRef = useRef<HTMLDivElement>(null);
  const newGroupInputRef = useRef<HTMLInputElement>(null);

  // Build userId -> groups mapping from memberships
  const userGroupsMap = useMemo(() => {
    const guestGroupsMap = new Map<number, Array<{ group_id: number; group_name: string; group_color: string | null }>>();
    for (const m of memberships) {
      if (!guestGroupsMap.has(m.guest_id)) {
        guestGroupsMap.set(m.guest_id, []);
      }
      guestGroupsMap.get(m.guest_id)!.push({
        group_id: m.group_id,
        group_name: m.group_name,
        group_color: m.group_color,
      });
    }

    const map = new Map<number, Array<{ group_id: number; group_name: string; group_color: string | null }>>();
    for (const row of guestRows) {
      if (!row.userId) continue;
      const userGroups: Array<{ group_id: number; group_name: string; group_color: string | null }> = [];
      const seenGroupIds = new Set<number>();
      for (const guest of row.eventGuests.values()) {
        if (!guest?.id) continue;
        const groups = guestGroupsMap.get(guest.id) || [];
        for (const g of groups) {
          if (!seenGroupIds.has(g.group_id)) {
            seenGroupIds.add(g.group_id);
            userGroups.push(g);
          }
        }
      }
      if (userGroups.length > 0) map.set(row.userId, userGroups);
    }
    return map;
  }, [guestRows, memberships]);

  // Filter guest rows for the selected group
  const displayedRows = useMemo(() => {
    let filtered = filteredGuestIds
      ? guestRows.filter((row) => {
          // Compare contactId (guest.id) against filtered guest IDs from group membership
          // Don't use eventGuests.values() because contactId may not be assigned to any event yet
          return filteredGuestIds.includes(row.contactId);
        })
      : guestRows;

    // Filter by selected events (OR logic)
    if (selectedEventIds.size > 0) {
      filtered = filtered.filter(row => {
        for (const eventId of selectedEventIds) {
          if (row.eventGuests.has(eventId)) return true;
        }
        return false;
      });
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      if (query.includes('#') && groups.length > 0) {
        const getContactIdsForGroup = (groupName: string): Set<number> => {
          const ids = new Set<number>();
          const matchingGroup = groups.find(g => g.name.toLowerCase() === groupName);
          if (!matchingGroup) return ids;
          if (matchingGroup.is_system_group) {
            if (matchingGroup.name === 'all') {
              for (const row of filtered) ids.add(row.contactId);
            } else if (matchingGroup.name.startsWith('rsvp-')) {
              const status = matchingGroup.name.replace('rsvp-', '');
              for (const row of filtered) {
                if (row.invitationStatus === status) ids.add(row.contactId);
              }
            }
          }
          return ids;
        };
        const segments: Array<{ groupName: string; operator: 'or' | 'and' }> = [];
        let normalized = query.replace(/\|/g, ' or ').replace(/,/g, ' or ').replace(/&/g, ' and ');
        const parts = normalized.split(/(\s+or\s+|\s+and\s+)/);
        let currentOperator: 'or' | 'and' = 'or';
        for (const part of parts) {
          const trimmed = part.trim();
          if (!trimmed) continue;
          if (/^or$/i.test(trimmed)) { currentOperator = 'or'; continue; }
          if (/^and$/i.test(trimmed)) { currentOperator = 'and'; continue; }
          const hashMatch = trimmed.match(/#([\w-]+)/);
          if (hashMatch) segments.push({ groupName: hashMatch[1], operator: currentOperator });
        }
        if (segments.length > 0) {
          let resultIds = getContactIdsForGroup(segments[0].groupName);
          for (let i = 1; i < segments.length; i++) {
            const seg = segments[i];
            const segIds = getContactIdsForGroup(seg.groupName);
            if (seg.operator === 'or') {
              for (const id of segIds) resultIds.add(id);
            } else {
              const intersection = new Set<number>();
              for (const id of resultIds) { if (segIds.has(id)) intersection.add(id); }
              resultIds = intersection;
            }
          }
          filtered = filtered.filter(row => resultIds.has(row.contactId));
        } else {
          filtered = [];
        }
      } else {
        filtered = filtered.filter(row =>
          row.name.toLowerCase().includes(query) || row.email?.toLowerCase().includes(query)
        );
      }
    }

    return filtered;
  }, [guestRows, filteredGuestIds, searchQuery, selectedEventIds, groups]);

  // Pagination
  const totalPages = Math.ceil(displayedRows.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedRows = displayedRows.slice(startIndex, endIndex);

  // Close group menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (groupMenuRef.current && !groupMenuRef.current.contains(e.target as Node)) {
        setShowGroupMenu(false);
        setCreatingNewGroup(false);
        setNewGroupName('');
      }
    };
    if (showGroupMenu) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showGroupMenu]);

  // Auto-focus new group input
  useEffect(() => {
    if (creatingNewGroup && newGroupInputRef.current) {
      newGroupInputRef.current.focus();
    }
  }, [creatingNewGroup]);

  // Selection helpers
  const toggleSelection = (contactId: number) => {
    setSelectedContactIds(prev => {
      const next = new Set(prev);
      if (next.has(contactId)) next.delete(contactId);
      else next.add(contactId);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedContactIds(new Set(displayedRows.map(r => r.contactId)));
  };

  const unselectAll = () => {
    setSelectedContactIds(new Set());
  };

  const discardSelection = () => {
    setSelectedContactIds(new Set());
  };

  // Get all guest_ids for selected contact rows
  const getGuestIdsForSelected = (): number[] => {
    const guestIds: number[] = [];
    for (const row of guestRows) {
      if (!selectedContactIds.has(row.contactId)) continue;
      for (const guest of row.eventGuests.values()) {
        if (guest?.id) guestIds.push(guest.id);
      }
    }
    return [...new Set(guestIds)];
  };

  // Custom (non-system) groups for the "Add to Group" menu
  const customGroups = useMemo(() =>
    groups.filter(g => !g.is_system_group),
    [groups]
  );

  const handleAddToGroup = async (groupId: number) => {
    const guestIds = getGuestIdsForSelected();
    if (guestIds.length === 0) return;
    setSavingToGroup(true);
    try {
      // Fetch current members, merge with selected, set
      const currentMembers = await groupService.getMembers(celebrationId, groupId);
      const currentIds = new Set(currentMembers.map((m: any) => m.guest_id));
      for (const id of guestIds) currentIds.add(id);
      await groupService.setMembers(celebrationId, groupId, [...currentIds]);
      setShowGroupMenu(false);
      setSelectedContactIds(new Set());
      setGroupRefreshKey(k => k + 1);
      onGroupsChanged();
    } catch {
      alert(t('groups.errors.addToGroup'));
    } finally {
      setSavingToGroup(false);
    }
  };

  const handleCreateAndAddToGroup = async () => {
    const name = newGroupName.trim();
    if (!name) return;
    const guestIds = getGuestIdsForSelected();
    if (guestIds.length === 0) return;
    setSavingToGroup(true);
    try {
      await groupService.createGroup(celebrationId, { name, guest_ids: guestIds });
      setShowGroupMenu(false);
      setCreatingNewGroup(false);
      setNewGroupName('');
      setSelectedContactIds(new Set());
      setGroupRefreshKey(k => k + 1);
      onGroupsChanged();
    } catch {
      alert(t('groups.errors.createGroup'));
    } finally {
      setSavingToGroup(false);
    }
  };

  const handleManageMembers = async (groupId: number, groupName: string) => {
    if (managingGroupId === groupId) {
      setManagingGroupId(null);
      setManagingGroupName('');
      setGroupMemberIds([]);
      return;
    }
    setManagingGroupId(groupId);
    setManagingGroupName(groupName);
    try {
      const members = await groupService.getMembers(celebrationId, groupId);
      setGroupMemberIds(members.map((m: any) => m.guest_id));
    } catch {
      setGroupMemberIds([]);
    }
  };

  const handleSaveGroupMembers = async () => {
    if (!managingGroupId) return;
    setSavingMembers(true);
    try {
      await groupService.setMembers(celebrationId, managingGroupId, groupMemberIds);
      setManagingGroupId(null);
      setManagingGroupName('');
      setGroupMemberIds([]);
      setGroupRefreshKey(k => k + 1);
      onGroupsChanged();
    } catch {
      alert(t('groups.errors.saveGroupMembers'));
    } finally {
      setSavingMembers(false);
    }
  };

  const handleCancelGroupMembers = () => {
    setManagingGroupId(null);
    setManagingGroupName('');
    setGroupMemberIds([]);
  };

  // When a group badge is clicked, show guests inline
  const handleFilterByGroup = (guestIds: number[] | null, groupQuery: string) => {
    if (!groupQuery) {
      setActiveGroupQuery('');
      setFilteredGuestIds(null);
    } else {
      setActiveGroupQuery(groupQuery);
      setFilteredGuestIds(guestIds);
    }
    setSearchQuery('');
    setSelectedEventIds(new Set());
    setCurrentPage(1);
  };

  const getInvitationIcon = (status: string) => {
    switch (status) {
      case 'accepted': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'declined': return <XCircle className="w-4 h-4 text-red-600" />;
      case 'invited': return <Clock className="w-4 h-4 text-blue-600" />;
      case 'new': return <UserPlus className="w-4 h-4 text-gray-600" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'accepted': return t('groups.status.accepted');
      case 'declined': return t('groups.status.declined');
      case 'invited': return t('groups.status.invited');
      case 'new': return t('groups.status.new');
      default: return status;
    }
  };

  const activeGroupObj = activeGroupQuery
    ? groups.find(g => g.name === activeGroupQuery.replace(/^#/, ''))
    : null;

  return (
    <div>
      {onAddGuests && (
        <div className="mb-4 flex justify-end">
          <Button size="sm" onClick={onAddGuests}>
            <UserPlus className="mr-1 h-4 w-4" />
            Add guests
          </Button>
        </div>
      )}

      {/* Group Manager */}
      <div className="mb-4 p-4 bg-white rounded-lg shadow border">
        <GroupManager
          celebrationId={celebrationId}
          onFilterByGroup={handleFilterByGroup}
          activeGroupQuery={activeGroupQuery}
          onManageMembers={handleManageMembers}
          managingGroupId={managingGroupId}
          key={groupRefreshKey}
        />
      </div>

      {/* Shared Search Bar with Event Filters */}
      <GuestSearchBar
        searchQuery={searchQuery}
        onSearchChange={(q) => { setSearchQuery(q); setCurrentPage(1); }}
        events={events}
        selectedEventIds={selectedEventIds}
        onToggleEvent={(eventId) => {
          setSelectedEventIds(prev => {
            const next = new Set(prev);
            if (next.has(eventId)) next.delete(eventId);
            else next.add(eventId);
            return next;
          });
          setCurrentPage(1);
        }}
        onClearEvents={() => { setSelectedEventIds(new Set()); setCurrentPage(1); }}
        groups={groups}
        resultCount={displayedRows.length}
      />

      {/* Member Editing Panel */}
      {managingGroupId && (
        <div className="mt-4 p-4 bg-white rounded-lg shadow border">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">
              {t('groups.editingMembersFor')} <span className="text-blue-600">#{managingGroupName}</span>
            </h3>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSaveGroupMembers} disabled={savingMembers}>
                {savingMembers ? t('groups.actions.saving') : t('groups.actions.saveMembers', { count: groupMemberIds.length })}
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCancelGroupMembers}>
                {t('groups.actions.cancel')}
              </Button>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto border rounded-md divide-y">
            {guestRows.map((row) => (
              <label
                key={row.contactId}
                className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={row.userId !== undefined && groupMemberIds.includes(row.userId)}
                  onChange={(e) => {
                    if (!row.userId) return;
                    const userId = row.userId;
                    setGroupMemberIds(prev =>
                      e.target.checked ? [...prev, userId] : prev.filter(id => id !== userId)
                    );
                  }}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">{row.name}</span>
                  {row.email && (
                    <span className="text-xs text-gray-500 ml-2">{row.email}</span>
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Guest List with Checkboxes */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            {selectedContactIds.size > 0 && (
              <span className="text-sm text-blue-600 font-medium">
                {t('groups.selectedGuestsCount', { count: selectedContactIds.size })}
              </span>
            )}

            {activeGroupObj && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">{t('groups.inLabel')}</span>
                <GroupBadge
                  name={activeGroupObj.name}
                  color={activeGroupObj.color}
                  isSystem={activeGroupObj.is_system_group}
                  count={displayedRows.length}
                  active
                />
                <button
                  onClick={() => handleFilterByGroup(null, '')}
                  className="text-xs text-blue-600 hover:underline"
                >
                  {t('groups.actions.clear')}
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Add to Group button - always visible */}
            <div className="relative" ref={groupMenuRef}>
              <Button
                size="sm"
                onClick={() => setShowGroupMenu(!showGroupMenu)}
                disabled={savingToGroup || selectedContactIds.size === 0}
                className={selectedContactIds.size > 0
                  ? "bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"}
              >
                {savingToGroup ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> {t('groups.actions.saving')}</>
                ) : (
                  <><FolderPlus className="h-3.5 w-3.5 mr-1" /> {t('groups.actions.addToGroup', { count: selectedContactIds.size })}</>
                )}
              </Button>

                {showGroupMenu && (
                  <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1">
                    {customGroups.length > 0 && (
                      <>
                        <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                          {t('groups.existingGroups')}
                        </div>
                        {customGroups.map(g => (
                          <button
                            key={g.id}
                            onClick={() => handleAddToGroup(g.id)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors"
                          >
                            <GroupBadge name={g.name} color={g.color} size="sm" />
                            {g.member_count !== undefined && (
                              <span className="text-[10px] text-gray-400 ml-auto">{g.member_count}</span>
                            )}
                          </button>
                        ))}
                        <div className="border-t border-gray-100 my-1" />
                      </>
                    )}

                    {!creatingNewGroup ? (
                      <button
                        onClick={() => setCreatingNewGroup(true)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 text-blue-600 flex items-center gap-2 transition-colors"
                      >
                        <Plus className="w-4 h-4" /> {t('groups.actions.newGroup')}
                      </button>
                    ) : (
                      <div className="px-3 py-2">
                        <div className="flex gap-1">
                          <input
                            ref={newGroupInputRef}
                            type="text"
                            placeholder={t('groups.placeholders.groupName')}
                            value={newGroupName}
                            onChange={(e) => setNewGroupName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && newGroupName.trim()) handleCreateAndAddToGroup();
                              if (e.key === 'Escape') { setCreatingNewGroup(false); setNewGroupName(''); }
                            }}
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <Button
                            size="sm"
                            onClick={handleCreateAndAddToGroup}
                            disabled={!newGroupName.trim() || savingToGroup}
                            className="px-2 py-1 h-auto"
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
          </div>
        </div>

        {/* Showing count */}
        <div className="mb-3 text-sm text-gray-600">
          {t('groups.showing', {
            from: displayedRows.length > 0 ? startIndex + 1 : 0,
            to: Math.min(endIndex, displayedRows.length),
            total: displayedRows.length,
          })}
        </div>

        {/* Guest Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="sticky left-0 z-10 bg-gray-50 w-24 px-2 py-3 border-r border-gray-200">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={selectAll}
                        title="Select all displayed guests"
                        className="p-0.5 rounded hover:bg-green-100 text-gray-400 hover:text-green-600 transition-colors"
                      >
                        <CheckSquare className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={unselectAll}
                        title="Unselect all guests"
                        className="p-0.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Square className="w-3.5 h-3.5" />
                      </button>
                      {selectedContactIds.size > 0 && (
                        <button
                          onClick={discardSelection}
                          title="Discard selection"
                          className="p-0.5 rounded hover:bg-amber-100 text-amber-500 hover:text-amber-700 transition-colors"
                        >
                          <Undo2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </th>
                  <th className="sticky left-10 z-10 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                    {t('groups.columns.guestName')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                    {t('groups.columns.groups')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                    {t('groups.columns.events')}
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                    {t('groups.columns.rsvp')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('groups.columns.host')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      {searchQuery ? t('groups.empty.search') : t('groups.empty.default')}
                    </td>
                  </tr>
                ) : (
                  paginatedRows.map((row) => {
                    const rowGroups = row.userId ? (userGroupsMap.get(row.userId) || []) : [];
                    const rowEvents = events.filter(e => row.eventGuests.has(e.id));
                    const isSelected = selectedContactIds.has(row.contactId);

                    return (
                      <tr
                        key={row.contactId}
                        className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50/50' : ''}`}
                      >
                        {/* Checkbox */}
                        <td className={`sticky left-0 z-10 w-10 px-3 py-4 border-r border-gray-200 ${isSelected ? 'bg-blue-50/50' : 'bg-white'}`}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelection(row.contactId)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                        </td>

                        {/* Guest Name */}
                        <td className={`sticky left-10 z-10 px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200 ${isSelected ? 'bg-blue-50/50' : 'bg-white'}`}>
                          <UserProfilePopover
                            user={{
                              id: row.userId || 0,
                              full_name: row.name,
                              email: row.email || '',
                              phone: row.phone,
                            }}
                          >
                            <span className="hover:underline cursor-pointer">
                              {row.name}
                              {row.nickname && (
                                <span className="text-xs text-gray-500 ml-1">({row.nickname})</span>
                              )}
                            </span>
                          </UserProfilePopover>
                        </td>

                        {/* Groups */}
                        <td className="px-6 py-4 text-sm border-r border-gray-200">
                          <div className="flex flex-wrap gap-1">
                            {rowGroups.length > 0 ? (
                              rowGroups.map((g) => (
                                <GroupBadge
                                  key={g.group_id}
                                  name={g.group_name}
                                  color={g.group_color || undefined}
                                  size="sm"
                                  active={activeGroupQuery === `#${g.group_name}`}
                                />
                              ))
                            ) : (
                              <span className="text-xs text-gray-400">{t('groups.values.empty')}</span>
                            )}
                          </div>
                        </td>

                        {/* Events */}
                        <td className="px-6 py-4 text-sm border-r border-gray-200">
                          <div className="flex flex-wrap gap-1">
                            {rowEvents.length > 0 ? (
                              rowEvents.map((e) => (
                                <span
                                  key={e.id}
                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 border border-gray-200"
                                >
                                  {e.title}
                                </span>
                              ))
                            ) : (
                              <span className="text-xs text-gray-400">{t('groups.values.empty')}</span>
                            )}
                          </div>
                        </td>

                        {/* RSVP */}
                        <td className="px-6 py-4 whitespace-nowrap text-center border-r border-gray-200">
                          <div className="flex items-center justify-center gap-1">
                            {getInvitationIcon(row.invitationStatus)}
                            <span className="text-xs text-gray-600">{getStatusLabel(row.invitationStatus)}</span>
                          </div>
                        </td>

                        {/* Host */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {row.creator_nickname ? (
                            <span className="text-xs text-gray-500">({row.creator_nickname})</span>
                          ) : (
                            <span className="text-xs text-gray-400">{t('groups.values.empty')}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {t('groups.pagination.page', { current: currentPage, total: totalPages })}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                  <ChevronLeft className="h-4 w-4 mr-1" /> {t('groups.pagination.previous')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                  {t('groups.pagination.next')} <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
